import { PerspectiveCamera } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useSceneRef } from "../../../RefsProvider"
import * as THREE from "three"
import { useEffect, useMemo, useRef } from "react"
import { useIsMobile } from "../../../utils/useIsMobile"


export default function CameraFour({ innerRef, id, off }) {

  const isMobile = useIsMobile()
  const camOffset = useMemo(
    () => (isMobile ? new THREE.Vector3(0, 30, -9) : new THREE.Vector3(0, 22, -9)),
    [isMobile]
  )

  const camTarget = useRef(new THREE.Vector3())
  const camPos = useRef(new THREE.Vector3())
  const temp = useRef(new THREE.Vector3())

  const refOvni = useSceneRef("ovni")

  const fogHeight = isMobile ? 2 : 5.4

  useEffect(() => {
    if (!off) return

    off.traverse((obj) => {
      if (!obj.isMesh) return
      const material = obj.material

      const handleMat = (mat) => {
        if (!mat) return

        if (!mat.fog) return

        if (!mat.userData?.heightFogPatched) {
          mat.onBeforeCompile = (shader) => {
            const vsHasFogPars = shader.vertexShader.includes("#include <fog_pars_vertex>")
            const vsHasFog = shader.vertexShader.includes("#include <fog_vertex>")
            const fsHasFogPars = shader.fragmentShader.includes("#include <fog_pars_fragment>")
            const fsHasDithering = shader.fragmentShader.includes("#include <dithering_fragment>")

            if (!(vsHasFogPars && vsHasFog && fsHasFogPars && fsHasDithering)) {
              return
            }

            // --- VERTEX : on ajoute notre varying + worldPos ---
            shader.vertexShader = shader.vertexShader
              .replace(
                "#include <fog_pars_vertex>",
                `#include <fog_pars_vertex>
                 varying vec3 vFogWorldPosition;`
              )
              .replace(
                "#include <fog_vertex>",
                `#include <fog_vertex>
                 vec4 fogWorldPosition = modelMatrix * vec4( transformed, 1.0 );
                 vFogWorldPosition = fogWorldPosition.xyz;`
              )

            // --- FRAGMENT : on ajoute uniform + mélange height-fog ---
            shader.fragmentShader = shader.fragmentShader
              .replace(
                "#include <fog_pars_fragment>",
                `#include <fog_pars_fragment>
                 uniform float fogHeight;
                 varying vec3 vFogWorldPosition;`
              )
              .replace(
                "#include <dithering_fragment>",
                `#include <dithering_fragment>
                 // Height fog : y proche de 0 = fog max, y proche de fogHeight = fog min
                 float h = clamp(vFogWorldPosition.y / fogHeight, 0.0, 1.0);
                 float fogAmount = 1.0 - h;
                 gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, fogAmount);`
              )

            shader.uniforms.fogHeight = { value: fogHeight }
            mat.userData.fogHeightUniform = shader.uniforms.fogHeight
          }

          mat.userData.heightFogPatched = true
          mat.needsUpdate = true
        }

        // 2) Si déjà patché, on met à jour l'uniform avec la valeur Leva
        const u = mat.userData?.fogHeightUniform
        if (u) {
          u.value = fogHeight
        }
      }

      if (Array.isArray(material)) {
        material.forEach(handleMat)
      } else {
        handleMat(material)
      }
    })
  }, [off, fogHeight])

  const refOvniVisual = useSceneRef("ovniVisual")

  useFrame((_, delta) => {
    if (!innerRef.current || !refOvniVisual.current) return

    delta = Math.min(delta, 1 / 30)

    // position rendue (smooth) plutôt que position physique (step)
    refOvniVisual.current.getWorldPosition(temp.current)

    const lerpFactor = 1 - Math.exp(-25 * delta)

    camTarget.current.lerp(temp.current, lerpFactor)

    const desiredPos = camPos.current.copy(camTarget.current).add(camOffset)

    innerRef.current.position.copy(desiredPos)
    innerRef.current.lookAt(camTarget.current)
  })



  return (
    <PerspectiveCamera
      ref={innerRef}
      position={[0, 22, -9]}
      fov={40}
      near={0.1}
    />
  )
}
